import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateDistrictManager } from "@/api/reseller/mutations";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createManagerSchema } from "@/lib/schemas/manager.schema";
import { getDrfFieldErrors } from "@/utils/errors";

type CreateManagerDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

export function CreateManagerDialog({
	open,
	onOpenChange,
}: CreateManagerDialogProps) {
	const { t } = useTranslation();
	const [email, setEmail] = useState("");
	const [companyName, setCompanyName] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const create = useCreateDistrictManager();

	useEffect(() => {
		if (open) {
			setEmail("");
			setCompanyName("");
			setFieldErrors({});
		}
	}, [open]);

	function handleSubmit() {
		setFieldErrors({});
		const result = createManagerSchema.safeParse({
			email: email.trim(),
			company_name: companyName.trim(),
		});
		if (!result.success) {
			toast.error(
				result.error.issues[0]?.message ?? t("common.validationError"),
			);
			return;
		}
		create.mutate(
			{ email: email.trim(), company_name: companyName.trim() },
			{
				onSuccess: () => {
					toast.success(t("managers.created"));
					onOpenChange(false);
				},
				onError: (error) => {
					if (error.message === "rate_limited") return;
					setFieldErrors(getDrfFieldErrors(error));
					toast.error(t("managers.createError"));
				},
			},
		);
	}

	const canSubmit =
		email.trim() &&
		companyName.trim() &&
		!create.isPending &&
		!create.isRateLimited;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Utwórz zarządcę</DialogTitle>
					<DialogDescription>
						Podaj dane nowego zarządcy osiedla
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="manager-email">E-mail</Label>
						<Input
							id="manager-email"
							type="email"
							name="email"
							autoComplete="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="np. jan@firma.pl"
							aria-invalid={!!fieldErrors.email}
							aria-describedby={
								fieldErrors.email ? "manager-email-error" : undefined
							}
						/>
						{fieldErrors.email && (
							<p
								id="manager-email-error"
								className="text-destructive text-sm"
								role="alert"
							>
								{fieldErrors.email}
							</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="manager-company">Nazwa firmy</Label>
						<Input
							id="manager-company"
							name="company"
							autoComplete="organization"
							value={companyName}
							onChange={(e) => setCompanyName(e.target.value)}
							placeholder="np. Zarządzanie Nieruchomościami Sp. z o.o."
							onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
							aria-invalid={!!fieldErrors.company_name}
							aria-describedby={
								fieldErrors.company_name ? "manager-company-error" : undefined
							}
						/>
						{fieldErrors.company_name && (
							<p
								id="manager-company-error"
								className="text-destructive text-sm"
								role="alert"
							>
								{fieldErrors.company_name}
							</p>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button
						size="sm"
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={create.isPending}
					>
						Anuluj
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{create.isPending ? "Tworzenie…" : "Utwórz"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
